"use client";

import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { releaseNoteQueryKeys } from "./release-note-query-keys";
import {
  getReleaseNotes,
  getReleaseNoteBySlug,
  getAvailableCategories,
  type GetReleaseNotesRequestParameter,
  type GetReleaseNotesResponse,
  type GetReleaseNoteBySlugResponse,
  type GetAvailableCategoriesResponse,
} from "@/app/[locale]/release-notes/_api/release-note-reads";

/**
 * Hook to fetch paginated release notes with filtering
 */
export function useReleaseNotes(
  params: GetReleaseNotesRequestParameter,
  options?: Omit<UseQueryOptions<GetReleaseNotesResponse>, "queryKey" | "queryFn">
) {
  const safeParams = params || {};
  return useQuery({
    queryKey: releaseNoteQueryKeys.list(safeParams),
    queryFn: () => getReleaseNotes(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to fetch single release note by slug
 */
export function useReleaseNoteDetail(
  slug: string,
  options?: Omit<UseQueryOptions<GetReleaseNoteBySlugResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: releaseNoteQueryKeys.detail(slug),
    queryFn: () => getReleaseNoteBySlug(slug),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!slug,
    ...options,
  });
}

/**
 * Hook to fetch all available release note categories
 */
export function useReleaseNoteCategories(
  options?: Omit<UseQueryOptions<GetAvailableCategoriesResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: releaseNoteQueryKeys.categories(),
    queryFn: () => getAvailableCategories(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
}